/*
 * @Description: 定位设置组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-10-27 10:53:50
 */
import {
  useState,
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react'
import React from 'react'
import { Switch, TimePicker, Popover, message } from 'antd'
import dayjs from 'dayjs'
import './index.scss'
import alertBgImage from '../../images/member-location-alert.png'
import CommonModal from '../CommonModal'

interface LocationSettingsProps {
  onLocationToggle?: (
    enabled: boolean,
    startTime: string,
    endTime: string
  ) => void
  onTimeRangeChange?: (startTime: string, endTime: string) => void
  sensorsClick?: (
    actionType: string,
    params?: { [key: string]: string }
  ) => void
  locationEnabled?: boolean
  startTime?: string
  endTime?: string
}

export interface LocationSettingsRef {
  handleErrorModalOk: () => void
}

const LocationSettings = forwardRef<LocationSettingsRef, LocationSettingsProps>(
  (
    {
      onLocationToggle,
      onTimeRangeChange,
      sensorsClick,
      locationEnabled: propLocationEnabled = false,
      startTime: propStartTime = '00:00',
      endTime: propEndTime = '23:59'
    },
    ref
  ) => {
    const [locationEnabled, setLocationEnabled] = useState(propLocationEnabled)
    const [startTime, setStartTime] = useState(propStartTime)
    const [endTime, setEndTime] = useState(propEndTime)

    // 错误弹窗状态
    const [showErrorModal, setShowErrorModal] = useState(false)

    // 引导状态
    const [guideStep, setGuideStep] = useState(0) // 0: 不显示, 1: 开始时间, 2: 结束时间, 3: 开启定位
    const [showGuide, setShowGuide] = useState(false)

    // 引用元素
    const startTimeRef = useRef<any>(null)
    const endTimeRef = useRef<any>(null)
    const switchRef = useRef<any>(null)

    // 监听props变化，更新本地状态
    useEffect(() => {
      setLocationEnabled(propLocationEnabled)
    }, [propLocationEnabled])

    useEffect(() => {
      setStartTime(propStartTime)
    }, [propStartTime])

    useEffect(() => {
      setEndTime(propEndTime)
    }, [propEndTime])

    // 验证时间段是否设置
    const validateTimeRange = useCallback(() => {
      return startTime !== '00:00' || endTime !== '23:59'
    }, [startTime, endTime])

    // 验证结束时间是否大于开始时间
    const validateTimeOrder = useCallback((start: string, end: string) => {
      if (start === '00:00' || end === '23:59') {
        return true // 如果有任一时间未设置，则不进行验证
      }
      const startMoment = dayjs(start, 'HH:mm')
      const endMoment = dayjs(end, 'HH:mm')
      return endMoment.isAfter(startMoment)
    }, [])

    // 计算Switch是否应该被禁用
    const isSwitchDisabled = useCallback(() => {
      // 如果时间段未设置，不禁用Switch（让用户可以开启后设置时间）
      if (!validateTimeRange()) {
        return false
      }
      // 如果时间段已设置但顺序错误，则禁用Switch
      return !validateTimeOrder(startTime, endTime)
    }, [startTime, endTime, validateTimeRange, validateTimeOrder])

    // 获取开始时间的禁用时间函数
    const getStartTimeDisabledHours = useCallback(() => {
      if (endTime === '00:00') return []
      const endMoment = dayjs(endTime, 'HH:mm')
      const endHour = endMoment.hour()
      const disabledHours: number[] = []
      for (let i = endHour; i < 24; i++) {
        disabledHours.push(i)
      }
      return disabledHours
    }, [endTime])

    const getStartTimeDisabledMinutes = useCallback(
      (selectedHour: number) => {
        if (endTime === '00:00') return []
        const endMoment = dayjs(endTime, 'HH:mm')
        const endHour = endMoment.hour()
        const endMinute = endMoment.minute()

        if (selectedHour === endHour) {
          const disabledMinutes: number[] = []
          for (let i = endMinute; i < 60; i++) {
            disabledMinutes.push(i)
          }
          return disabledMinutes
        }
        return []
      },
      [endTime]
    )

    // 获取结束时间的禁用时间函数
    const getEndTimeDisabledHours = useCallback(() => {
      if (startTime === '00:00') return []
      const startMoment = dayjs(startTime, 'HH:mm')
      const startHour = startMoment.hour()
      const disabledHours: number[] = []
      for (let i = 0; i <= startHour; i++) {
        disabledHours.push(i)
      }
      return disabledHours
    }, [startTime])

    const getEndTimeDisabledMinutes = useCallback(
      (selectedHour: number) => {
        if (startTime === '00:00') return []
        const startMoment = dayjs(startTime, 'HH:mm')
        const startHour = startMoment.hour()
        const startMinute = startMoment.minute()

        if (selectedHour === startHour) {
          const disabledMinutes: number[] = []
          for (let i = 0; i <= startMinute; i++) {
            disabledMinutes.push(i)
          }
          return disabledMinutes
        }
        return []
      },
      [startTime]
    )

    // 处理定位开关变化
    const handleLocationToggle = useCallback(
      (checked: boolean) => {
        if (checked) {
          // 验证时间顺序是否正确
          if (!validateTimeOrder(startTime, endTime)) {
            // 如果开始时间大于等于结束时间，显示错误提示
            message.error('结束时间应该大于开始时间，请重新设置时间')
            return
          }
        }

        setLocationEnabled(checked)
        if (onLocationToggle) {
          onLocationToggle(checked, startTime, endTime)
        }

        // 埋点统计
        if (sensorsClick) {
          sensorsClick(checked ? 'enableLocation' : 'disableLocation')
        }
      },
      [
        onLocationToggle,
        sensorsClick,
        validateTimeRange,
        validateTimeOrder,
        startTime,
        endTime
      ]
    )

    // 处理开始时间变化
    const handleStartTimeChange = useCallback(
      (time: any) => {
        if (time) {
          const timeStr = time.format('HH:mm')

          // 验证开始时间是否小于结束时间
          if (endTime && !validateTimeOrder(timeStr, endTime)) {
            // 如果开始时间不小于结束时间，不更新状态
            return
          }

          setStartTime(timeStr)
          if (onTimeRangeChange) {
            onTimeRangeChange(timeStr, endTime)
          }

          // 埋点统计
          if (sensorsClick) {
            sensorsClick('changeStartTime', { time: timeStr })
          }

          // 如果正在引导中，自动进入下一步
          if (showGuide && guideStep === 1) {
            setGuideStep(2)
          }
        }
      },
      [
        endTime,
        onTimeRangeChange,
        sensorsClick,
        showGuide,
        guideStep,
        validateTimeOrder
      ]
    )

    // 处理结束时间变化
    const handleEndTimeChange = useCallback(
      (time: any) => {
        if (time) {
          const timeStr = time.format('HH:mm')

          // 验证结束时间是否大于开始时间
          if (startTime && !validateTimeOrder(startTime, timeStr)) {
            // 如果结束时间不大于开始时间，不更新状态
            return
          }

          setEndTime(timeStr)
          if (onTimeRangeChange) {
            onTimeRangeChange(startTime, timeStr)
          }

          // 埋点统计
          if (sensorsClick) {
            sensorsClick('changeEndTime', { time: timeStr })
          }

          // 如果正在引导中，自动进入下一步
          if (showGuide && guideStep === 2) {
            setGuideStep(3)
          }
        }
      },
      [
        startTime,
        onTimeRangeChange,
        sensorsClick,
        showGuide,
        guideStep,
        validateTimeOrder
      ]
    )

    // 处理错误弹窗确认
    const handleErrorModalOk = useCallback(() => {
      setShowErrorModal(false)
      // 开始引导流程
      setShowGuide(true)
      setGuideStep(1)
    }, [])

    // 处理引导步骤变化
    const handleGuideStepChange = useCallback((step: number) => {
      if (step > 3) {
        // 引导完成
        setShowGuide(false)
        setGuideStep(0)
      } else {
        setGuideStep(step)
      }
    }, [])

    // 暴露方法给父组件
    useImperativeHandle(
      ref,
      () => ({
        handleErrorModalOk
      }),
      [handleErrorModalOk]
    )

    return (
      <div className="location-settings-wrapper">
        <div className="location-settings">
          <div className="header-row">
            <div className="time-range-group">
              <span className="time-range-label">实时定位时间段</span>
              <Popover
                content="请设置开始时间"
                open={showGuide && guideStep === 1}
                placement="bottom"
                classNames={{ root: 'location-guide-popover' }}
              >
                <TimePicker
                  ref={startTimeRef}
                  value={startTime ? dayjs(startTime, 'HH:mm') : undefined}
                  onChange={handleStartTimeChange}
                  format="HH:mm"
                  placeholder="开始时间"
                  size="small"
                  disabledHours={getStartTimeDisabledHours}
                  disabledMinutes={getStartTimeDisabledMinutes}
                />
              </Popover>
              <span className="time-separator">-</span>
              <Popover
                content="请设置结束时间"
                open={showGuide && guideStep === 2}
                placement="bottom"
                classNames={{ root: 'location-guide-popover' }}
              >
                <TimePicker
                  ref={endTimeRef}
                  value={endTime ? dayjs(endTime, 'HH:mm') : undefined}
                  onChange={handleEndTimeChange}
                  format="HH:mm"
                  placeholder="结束时间"
                  size="small"
                  disabledHours={getEndTimeDisabledHours}
                  disabledMinutes={getEndTimeDisabledMinutes}
                />
              </Popover>
            </div>

            <div className="location-toggle-group">
              <span className="toggle-label">开启定位</span>
              <Popover
                content="现在可以开启定位了"
                open={showGuide && guideStep === 3}
                placement="bottom"
                classNames={{ root: 'location-guide-popover' }}
                onOpenChange={(open) => {
                  if (!open && showGuide && guideStep === 3) {
                    handleGuideStepChange(4)
                  }
                }}
              >
                <Switch
                  ref={switchRef}
                  checked={locationEnabled}
                  onChange={handleLocationToggle}
                  size="default"
                />
              </Popover>
            </div>
          </div>

          {/* 错误弹窗 */}
          <CommonModal
            open={showErrorModal}
            onCancel={() => setShowErrorModal(false)}
            onOk={handleErrorModalOk}
            title="开启失败"
            content="请先设置【实时定位时间段】"
            backgroundImage={alertBgImage}
          />
        </div>
      </div>
    )
  }
)

export default LocationSettings
